import type { Range } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { DecorationSet } from '@tiptap/pm/view';
import { getDecorations } from './decoration';
import { NODE_SEARCH_TARGETS, SKIP_TEXT_SEARCH_NODE_TYPE_SET, type NodeSearchSubAttr, type NodeSearchTarget } from './node-search-targets';

export interface SearchResult extends Range {
    nodeType: string;
    isReplaceable: boolean;
    matchedAttr?: string;
}

interface ProcessedSearches {
    decorationsToReturn: DecorationSet;
    results: SearchResult[];
    hasReplaceableMatch: boolean;
}

function collectTextSearchResults(text: string, pos: number, searchTerm: RegExp, results: SearchResult[]) {
    const matches = Array.from(text.matchAll(searchTerm)).filter(([matchText]) => matchText.trim());

    for (const match of matches) {
        if (match[0] === '') break;

        if (match.index !== undefined) {
            results.push({
                from: pos + match.index,
                to: pos + match.index + match[0].length,
                nodeType: 'text',
                isReplaceable: true,
            });
        }
    }
}

function findMatchInString(
    value: string,
    node: PMNode,
    attr: string,
    pos: number,
    searchTerm: RegExp,
    results: SearchResult[],
    target: NodeSearchTarget,
): boolean {
    const matches = Array.from(value.matchAll(searchTerm)).filter(([m]) => m.trim());
    if (target.maxOneMatch && matches.length > 1) {
        matches.splice(1);
    }
    for (const match of matches) {
        if (match[0] === '') break;
        results.push({
            from: pos,
            to: pos + node.nodeSize,
            nodeType: node.type.name,
            isReplaceable:
                target.attr_and_replaceable.find(attr_and_replaceable => attr_and_replaceable.attr === attr)?.replaceable ?? false,
            matchedAttr: attr,
        });
        if (target.maxOneMatch) {
            return true;
        }
    }
    return false;
}

function findMatchInArray(
    value: string[],
    node: PMNode,
    attr: string,
    pos: number,
    searchTerm: RegExp,
    results: SearchResult[],
    target: NodeSearchTarget,
): boolean {
    for (const item of value) {
        if (findMatchInString(item, node, attr, pos, searchTerm, results, target)) {
            return true;
        }
    }
    return false;
}

function findMatchInSubAttr(
    value: unknown[],
    subAttr: NodeSearchSubAttr,
    node: PMNode,
    pos: number,
    searchTerm: RegExp,
    results: SearchResult[],
    target: NodeSearchTarget,
): boolean {
    let foundMatch = false;

    for (const item of value) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const nestedValue = (item as Record<string, unknown>)[subAttr.field];
        if (typeof nestedValue !== 'string' || !nestedValue) {
            continue;
        }
        const matches = Array.from(nestedValue.matchAll(searchTerm)).filter(([m]) => m.trim());
        if (target.maxOneMatch && matches.length > 1) {
            matches.splice(1);
        }
        for (const match of matches) {
            if (match[0] === '') {
                break;
            }
            results.push({
                from: pos,
                to: pos + node.nodeSize,
                nodeType: node.type.name,
                isReplaceable: false,
            });
            foundMatch = true;
            if (target.maxOneMatch) {
                return true;
            }
        }
    }
    return foundMatch && target.maxOneMatch;
}

function collectNodeSearchResults(node: PMNode, pos: number, searchTerm: RegExp, results: SearchResult[], target: NodeSearchTarget) {
    if (target.skipTextSearch) {
        const text = node.textContent;
        if (text) {
            findMatchInString(text, node, '', pos, searchTerm, results, target);
        }
        return;
    }

    for (const attr_and_replaceable of target.attr_and_replaceable) {
        const value = node.attrs[attr_and_replaceable.attr];
        if (typeof value === 'string' && value) {
            if (findMatchInString(value, node, attr_and_replaceable.attr, pos, searchTerm, results, target)) {
                break;
            }
        } else if (Array.isArray(value) && value.length > 0) {
            if (findMatchInArray(value, node, attr_and_replaceable.attr, pos, searchTerm, results, target)) {
                break;
            }
        }
    }

    for (const subAttr of target.subAttrs ?? []) {
        const value = node.attrs[subAttr.attr];
        if (!Array.isArray(value) || value.length === 0) {
            continue;
        }
        if (findMatchInSubAttr(value, subAttr, node, pos, searchTerm, results, target)) {
            break;
        }
    }
}

export function processSearches(doc: PMNode, searchTerm: RegExp | null, resultIndex: number): ProcessedSearches {
    const results: SearchResult[] = [];

    if (!searchTerm) {
        return { decorationsToReturn: DecorationSet.empty, results: [], hasReplaceableMatch: false };
    }

    doc?.descendants((node, pos) => {
        const target = NODE_SEARCH_TARGETS.get(node.type.name);
        if (target) {
            collectNodeSearchResults(node, pos, searchTerm, results, target);
        }

        // Don't descend into nodes whose inner text shouldn't be searched (e.g. zyConsole, zyCode).
        if (SKIP_TEXT_SEARCH_NODE_TYPE_SET.has(node.type.name)) {
            return false;
        }

        if (node.isText && node.text) {
            collectTextSearchResults(node.text, pos, searchTerm, results);
        }

        return undefined;
    });

    const decorations = getDecorations(results, resultIndex);
    const decorationsToReturn = DecorationSet.create(doc, decorations);
    const hasReplaceableMatch = results.some(result => result.isReplaceable);

    return {
        decorationsToReturn,
        results,
        hasReplaceableMatch,
    };
}
